import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AutomationRule,
  AutomationRuleListResponse,
  AutomationRuleMeta,
} from '../model/automation-rule.model';

interface AutomationRuleDTO {
  id: number;
  cameraId: number;
  deviceId: number;
  detectionType: string;
  primaryAction: string;
  delaySeconds: number;
  delayedAction: string;
  isActive: number | boolean;
}

interface AutomationRuleListDTO {
  meta: AutomationRuleMeta;
  data: AutomationRuleDTO[];
}

@Injectable({ providedIn: 'root' })
export class AutomationRuleApiService {
  private readonly http = inject(HttpClient);

  getRules(page: number, maxResults: number): Observable<AutomationRuleListResponse> {
    const params = new HttpParams()
      .set('page', page)
      .set('maxResults', maxResults);

    return this.http
      .get<AutomationRuleListDTO>(`${environment.baseUrl}/iot-devices/rules`, {
        params,
        headers: this.getHeaders(),
      })
      .pipe(
        map((response) => ({
          meta: response.meta,
          rules: (response.data ?? []).map((rule) => this.toModel(rule)),
        })),
      );
  }

  createRule(payload: Omit<AutomationRule, 'id'>): Observable<unknown> {
    return this.http.post(`${environment.baseUrl}/iot-devices/rules`, payload, {
      headers: this.getHeaders(),
    });
  }

  updateRule(
    id: number,
    payload: Omit<AutomationRule, 'id'>,
  ): Observable<unknown> {
    return this.http.put(
      `${environment.baseUrl}/iot-devices/rules/${id}`,
      payload,
      { headers: this.getHeaders() },
    );
  }

  deleteRule(id: number): Observable<unknown> {
    return this.http.delete(`${environment.baseUrl}/iot-devices/rules/${id}`, {
      headers: this.getHeaders(),
    });
  }

  private toModel(dto: AutomationRuleDTO): AutomationRule {
    return {
      id: dto.id,
      cameraId: dto.cameraId,
      deviceId: dto.deviceId,
      detectionType: dto.detectionType,
      primaryAction: dto.primaryAction,
      delaySeconds: dto.delaySeconds,
      delayedAction: dto.delayedAction,
      isActive: !!dto.isActive,
    };
  }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({ Authorization: `Bearer ${token || ''}` });
  }
}
